import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SignatureThumbnail from './SignatureThumbnail';
import ConfirmDialog from './ConfirmDialog';
import VersionHistoryModal from './VersionHistoryModal';
import './SignatureCard.css';

function SignatureCard({
    signature,
    isSelected,
    onSelect,
    onSetDefault,
    onEdit,
    onDuplicate,
    onDelete,
    onToggleActive,
    onVersionRestore,
    onPreview,
    pendingChangesCount = 0,
    canUpdate = true,
    canDelete = true
}) {
    const navigate = useNavigate();
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const [showVersionHistory, setShowVersionHistory] = useState(false);
    const dropdownRef = useRef(null);

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const handleDelete = () => {
        setShowDeleteConfirm(true);
    };

    const handleConfirmDelete = () => {
        onDelete(signature.id);
        setShowDeleteConfirm(false);
    };

    const handleMenuAction = (action) => {
        setShowDropdown(false);
        switch (action) {
            case 'edit':
                onEdit(signature.id);
                break;
            case 'preview':
                onPreview(signature.id);
                break;
            case 'settings':
                navigate(`/signatures/${signature.id}/settings`);
                break;
            case 'duplicate':
                onDuplicate(signature.id);
                break;
            case 'history':
                setShowVersionHistory(true);
                break;
            case 'setDefault':
                onSetDefault(signature.id);
                break;
            case 'toggleActive':
                onToggleActive(signature.id);
                break;
            case 'delete':
                setShowDeleteConfirm(true);
                break;
            default:
                break;
        }
    };

    // Gérer le clic en dehors du dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        };

        if (showDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [showDropdown]);

    return (
        <>
            <div className={`signature-card ${isSelected ? 'selected' : ''}`}>
                <div className="card-header">
                    <label className="card-checkbox" onClick={(e) => e.stopPropagation()}>
                        <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => onSelect(signature.id)}
                        />
                    </label>

                    <div className="card-badges">
                        {signature.is_default && (
                            <div className="default-badge">
                                <svg
                                    width="16"
                                    height="16"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                >
                                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                </svg>
                                Défaut
                            </div>
                        )}
                        {pendingChangesCount > 0 && (
                            <div className="pending-changes-badge" title={`${pendingChangesCount} modification(s) planifiée(s) en attente`}>
                                ⏰ {pendingChangesCount}
                            </div>
                        )}
                    </div>
                </div>

                <div className="card-thumbnail" onClick={() => canUpdate ? onEdit(signature.id) : onPreview(signature.id)}>
                    <SignatureThumbnail
                        signatureId={signature.id}
                        uid={signature.uid}
                        updatedAt={signature.updated_at}
                        width={300}
                        height={200}
                    />
                </div>

                <div className="card-body">
                    <div className="card-title-row">
                        <h3 className="card-title" title={signature.name}>
                            {signature.name}
                        </h3>
                        <span className={`status-badge ${signature.is_active ? 'status-active' : 'status-inactive'}`}>
                            {signature.is_active ? 'Active' : 'Inactive'}
                        </span>
                    </div>
                    <div className="card-dates">
                        <div className="card-date">
                            <span className="date-label">Créée:</span>
                            <span className="date-value">{formatDate(signature.created_at)}</span>
                        </div>
                        <div className="card-date">
                            <span className="date-label">Modifiée:</span>
                            <span className="date-value">{formatDate(signature.updated_at)}</span>
                        </div>
                    </div>

                    {signature.creator_first_name && (
                        <div className="card-creator">
                            <svg
                                width="14"
                                height="14"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                                />
                            </svg>
                            <span className="creator-label">
                                {signature.is_owner ? 'Créée par vous' : `Créée par ${signature.creator_first_name} ${signature.creator_last_name || ''}`}
                            </span>
                        </div>
                    )}
                </div>

                <div className="card-footer">
                    <div className="card-actions-menu" ref={dropdownRef}>
                        <button
                            className="card-btn btn-menu"
                            onClick={() => setShowDropdown(!showDropdown)}
                            title="Actions"
                        >
                            <svg
                                width="16"
                                height="16"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
                                />
                            </svg>
                            Actions
                        </button>

                        {showDropdown && (
                            <div className="dropdown-menu">
                                {canUpdate && (
                                    <button
                                        className="dropdown-item dropdown-item-edit"
                                        onClick={() => handleMenuAction('edit')}
                                    >
                                        <svg
                                            width="16"
                                            height="16"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                            />
                                        </svg>
                                        Éditer
                                    </button>
                                )}

                                <button
                                    className="dropdown-item dropdown-item-preview"
                                    onClick={() => handleMenuAction('preview')}
                                >
                                    <svg
                                        width="16"
                                        height="16"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                        />
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                        />
                                    </svg>
                                    Aperçu
                                </button>

                                {canUpdate && (
                                    <>
                                        <button
                                            className="dropdown-item dropdown-item-settings"
                                            onClick={() => handleMenuAction('settings')}
                                        >
                                            <svg
                                                width="16"
                                                height="16"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                                                />
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                                />
                                            </svg>
                                            Paramètres
                                        </button>

                                        <button
                                            className="dropdown-item dropdown-item-duplicate"
                                            onClick={() => handleMenuAction('duplicate')}
                                        >
                                            <svg
                                                width="16"
                                                height="16"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                                                />
                                            </svg>
                                            Dupliquer
                                        </button>
                                    </>
                                )}

                                <button
                                    className="dropdown-item dropdown-item-history"
                                    onClick={() => handleMenuAction('history')}
                                >
                                    <svg
                                        width="16"
                                        height="16"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                        />
                                    </svg>
                                    Historique
                                </button>

                                {canUpdate && !signature.is_default && (
                                    <button
                                        className="dropdown-item dropdown-item-default"
                                        onClick={() => handleMenuAction('setDefault')}
                                    >
                                        <svg
                                            width="16"
                                            height="16"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
                                            />
                                        </svg>
                                        Définir par défaut
                                    </button>
                                )}

                                {canUpdate && (
                                    <button
                                        className={`dropdown-item dropdown-item-toggle ${signature.is_active ? 'active' : 'inactive'}`}
                                        onClick={() => handleMenuAction('toggleActive')}
                                    >
                                        <svg
                                            width="16"
                                            height="16"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M13 10V3L4 14h7v7l9-11h-7z"
                                            />
                                        </svg>
                                        {signature.is_active ? 'Désactiver' : 'Activer'}
                                    </button>
                                )}

                                {canDelete && (
                                    <button
                                        className="dropdown-item dropdown-item-delete"
                                        onClick={() => handleMenuAction('delete')}
                                    >
                                        <svg
                                            width="16"
                                            height="16"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                            />
                                        </svg>
                                        Supprimer
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <ConfirmDialog
                isOpen={showDeleteConfirm}
                title="Supprimer la signature"
                message={`Êtes-vous sûr de vouloir supprimer "${signature.name}" ? Cette action est irréversible.`}
                confirmText="Supprimer"
                cancelText="Annuler"
                onConfirm={handleConfirmDelete}
                onCancel={() => setShowDeleteConfirm(false)}
                danger={true}
            />

            <VersionHistoryModal
                isOpen={showVersionHistory}
                signatureId={signature.id}
                signatureName={signature.name}
                onClose={() => setShowVersionHistory(false)}
                onRestore={onVersionRestore}
                canUpdate={canUpdate}
            />
        </>
    );
}

export default SignatureCard;
